import { useState, useEffect } from 'react';
import API from '../api';

const fmt    = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const fmtPct = v => (v == null ? '—' : parseFloat(v).toFixed(1) + '%');

const currentYear = new Date().getFullYear();
const ANIOS = Array.from({ length: currentYear - 2024 }, (_, i) => currentYear - i);

const SORT_ICONS = { asc: ' ↑', desc: ' ↓', none: '' };

function SortHeader({ label, field, sort, onSort }) {
  const active = sort.field === field;
  return (
    <th onClick={() => onSort(field)} style={{
      padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: active ? '#00b8e3' : '#5a7a8a',
      cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      borderBottom: '1px solid var(--app-border)', textAlign: 'right',
    }}>
      {label}{SORT_ICONS[active ? sort.dir : 'none']}
    </th>
  );
}

function sortRows(rows, { field, dir }) {
  if (!field || !Array.isArray(rows)) return rows || [];
  return [...rows].sort((a, b) => {
    const va = parseFloat(a[field]) || 0, vb = parseFloat(b[field]) || 0;
    return dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });
}

function Bar({ value, max, color = '#00b8e3' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ flex: 1, height: 5, background: '#f4f7fa', borderRadius: 1, overflow: 'hidden', minWidth: 40 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 1 }} />
    </div>
  );
}

function TabClientes({ data }) {
  const [sort, setSort] = useState({ field: 'total_utilidad', dir: 'desc' });

  function onSort(field) {
    setSort(s => s.field === field
      ? { field, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { field, dir: 'desc' }
    );
  }

  const rows = sortRows(Array.isArray(data) ? data : [], sort);
  const maxValor     = rows.reduce((m, r) => Math.max(m, parseFloat(r.total_valor) || 0), 1);
  const maxUtilidad  = rows.reduce((m, r) => Math.max(m, parseFloat(r.total_utilidad) || 0), 1);
  const maxPendiente = rows.reduce((m, r) => Math.max(m, parseFloat(r.pendiente) || 0), 1);

  const totValor     = rows.reduce((s, r) => s + (parseFloat(r.total_valor) || 0), 0);
  const totUtilidad  = rows.reduce((s, r) => s + (parseFloat(r.total_utilidad) || 0), 0);
  const totPendiente = rows.reduce((s, r) => s + (parseFloat(r.pendiente) || 0), 0);

  return (
    <div>
      {/* Totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Total facturado', value: fmt(totValor), color: '#08293f', accent: true },
          { label: 'Utilidad total',  value: fmt(totUtilidad), color: '#059669' },
          { label: 'Por cobrar',      value: fmt(totPendiente), color: '#dc2626' },
        ].map((k, i) => (
          <div key={i} style={{
            background: k.accent ? '#08293f' : '#fff',
            border: k.accent ? '1px solid rgba(0,184,227,0.3)' : '1px solid #e8eef2',
            padding: '12px 14px', borderRadius: 2
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: k.accent ? 'rgba(0,184,227,0.6)' : '#5a7a8a', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: k.accent ? '#00b8e3' : k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Table desktop */}
      <div className="rent-tabla-desktop">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#5a7a8a', borderBottom: '1px solid var(--app-border)', textAlign: 'left' }}>
                Cliente
              </th>
              <SortHeader label="Registros"  field="registros"     sort={sort} onSort={onSort} />
              <SortHeader label="Facturado"  field="total_valor"   sort={sort} onSort={onSort} />
              <th style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#5a7a8a', borderBottom: '1px solid var(--app-border)' }}></th>
              <SortHeader label="Utilidad"   field="total_utilidad" sort={sort} onSort={onSort} />
              <th style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#5a7a8a', borderBottom: '1px solid var(--app-border)' }}></th>
              <SortHeader label="Margen"     field="margen_prom"   sort={sort} onSort={onSort} />
              <SortHeader label="Pendiente"  field="pendiente"     sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f4f7fa' }}>
                <td style={{ padding: '9px 10px', color: '#08293f', fontWeight: 600, fontSize: 12 }}>{r.cliente}</td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#5a7a8a' }}>{r.registros}</td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#08293f', fontWeight: 500 }}>{fmt(r.total_valor)}</td>
                <td style={{ padding: '9px 10px', width: 80 }}>
                  <Bar value={r.total_valor} max={maxValor} color="#00b8e3" />
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(r.total_utilidad)}</td>
                <td style={{ padding: '9px 10px', width: 80 }}>
                  <Bar value={r.total_utilidad} max={maxUtilidad} color="#059669" />
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 2,
                    background: r.margen_prom >= 50 ? 'rgba(5,150,105,0.08)' : r.margen_prom >= 20 ? 'rgba(251,191,36,0.1)' : 'rgba(248,113,113,0.08)',
                    color: r.margen_prom >= 50 ? '#059669' : r.margen_prom >= 20 ? '#b45309' : '#dc2626',
                  }}>{fmtPct(r.margen_prom)}</span>
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right',
                  color: r.pendiente > 0 ? '#dc2626' : '#5a7a8a',
                  fontWeight: r.pendiente > 0 ? 700 : 400 }}>{fmt(r.pendiente)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards mobile */}
      <div className="rent-cards-mobile">
        {rows.map((r, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: '#08293f', fontSize: 13 }}>{r.cliente}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 2,
                background: r.margen_prom >= 50 ? 'rgba(5,150,105,0.08)' : 'rgba(251,191,36,0.1)',
                color: r.margen_prom >= 50 ? '#059669' : '#b45309',
              }}>{fmtPct(r.margen_prom)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11 }}>
              <div><span style={{ color: '#5a7a8a' }}>Facturado</span><br /><b style={{ color: '#08293f' }}>{fmt(r.total_valor)}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Utilidad</span><br /><b style={{ color: '#059669' }}>{fmt(r.total_utilidad)}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Registros</span><br /><b>{r.registros}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Pendiente</span><br />
                <b style={{ color: r.pendiente > 0 ? '#dc2626' : '#5a7a8a' }}>{fmt(r.pendiente)}</b>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabCategorias({ data }) {
  const [sort, setSort] = useState({ field: 'total_utilidad', dir: 'desc' });

  function onSort(field) {
    setSort(s => s.field === field
      ? { field, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { field, dir: 'desc' }
    );
  }

  const rows = sortRows(Array.isArray(data) ? data : [], sort);
  const maxValor    = rows.reduce((m, r) => Math.max(m, parseFloat(r.total_valor) || 0), 1);
  const maxUtilidad = rows.reduce((m, r) => Math.max(m, parseFloat(r.total_utilidad) || 0), 1);

  return (
    <div>
      <div className="rent-tabla-desktop">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#5a7a8a', borderBottom: '1px solid var(--app-border)', textAlign: 'left' }}>
                Categoría
              </th>
              <SortHeader label="Registros"  field="registros"      sort={sort} onSort={onSort} />
              <SortHeader label="Facturado"  field="total_valor"    sort={sort} onSort={onSort} />
              <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--app-border)' }}></th>
              <SortHeader label="Utilidad"   field="total_utilidad" sort={sort} onSort={onSort} />
              <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--app-border)' }}></th>
              <SortHeader label="Margen"     field="margen_prom"    sort={sort} onSort={onSort} />
              <SortHeader label="Pendiente"  field="pendiente"      sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f4f7fa' }}>
                <td style={{ padding: '9px 10px', color: r.categoria === 'Sin categoría' ? '#94a3b8' : '#08293f', fontWeight: 600, fontStyle: r.categoria === 'Sin categoría' ? 'italic' : 'normal' }}>{r.categoria}</td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#5a7a8a' }}>{r.registros}</td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#08293f', fontWeight: 500 }}>{fmt(r.total_valor)}</td>
                <td style={{ padding: '9px 10px', width: 80 }}>
                  <Bar value={r.total_valor} max={maxValor} color="#00b8e3" />
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(r.total_utilidad)}</td>
                <td style={{ padding: '9px 10px', width: 80 }}>
                  <Bar value={r.total_utilidad} max={maxUtilidad} color="#059669" />
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 2,
                    background: r.margen_prom >= 50 ? 'rgba(5,150,105,0.08)' : r.margen_prom >= 20 ? 'rgba(251,191,36,0.1)' : 'rgba(248,113,113,0.08)',
                    color: r.margen_prom >= 50 ? '#059669' : r.margen_prom >= 20 ? '#b45309' : '#dc2626',
                  }}>{fmtPct(r.margen_prom)}</span>
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right',
                  color: r.pendiente > 0 ? '#dc2626' : '#5a7a8a',
                  fontWeight: r.pendiente > 0 ? 700 : 400 }}>{fmt(r.pendiente)}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} style={{ padding: '20px 10px', textAlign: 'center', color: '#5a7a8a' }}>Sin datos para este año</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rent-cards-mobile">
        {rows.map((r, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: '#08293f', fontSize: 13 }}>{r.categoria}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 2,
                background: r.margen_prom >= 50 ? 'rgba(5,150,105,0.08)' : 'rgba(251,191,36,0.1)',
                color: r.margen_prom >= 50 ? '#059669' : '#b45309',
              }}>{fmtPct(r.margen_prom)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11 }}>
              <div><span style={{ color: '#5a7a8a' }}>Facturado</span><br /><b style={{ color: '#08293f' }}>{fmt(r.total_valor)}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Utilidad</span><br /><b style={{ color: '#059669' }}>{fmt(r.total_utilidad)}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Registros</span><br /><b>{r.registros}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Pendiente</span><br />
                <b style={{ color: r.pendiente > 0 ? '#dc2626' : '#5a7a8a' }}>{fmt(r.pendiente)}</b>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TipoResumen({ data }) {
  const rows = Array.isArray(data) ? data : [];
  const fijo = rows.find(r => r.tipo === 'FIJO') || { total_valor: 0, total_utilidad: 0, registros: 0 };
  const particular = rows.find(r => r.tipo === 'PARTICULAR') || { total_valor: 0, total_utilidad: 0, registros: 0 };
  const total = (parseFloat(fijo.total_valor) || 0) + (parseFloat(particular.total_valor) || 0);
  const pctFijo = total > 0 ? (parseFloat(fijo.total_valor) || 0) / total * 100 : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 16 }}>
      {[
        { r: fijo, label: 'Clientes fijos (mensualidad)', color: '#08293f' },
        { r: particular, label: 'Trabajos particulares', color: '#0284c7' },
      ].map((k, i) => (
        <div key={i} style={{ background: '#fff', border: '1px solid #e8eef2', padding: '12px 14px', borderRadius: 2 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 4 }}>{k.label}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: k.color }}>{fmt(k.r.total_valor)}</span>
            <span style={{ fontSize: 11, color: '#5a7a8a' }}>{k.r.registros} registros · {fmtPct(k.r.margen_prom)} margen</span>
          </div>
        </div>
      ))}
      <div style={{ gridColumn: '1 / -1' }}>
        <Bar value={pctFijo} max={100} color="#08293f" />
      </div>
    </div>
  );
}

function TabServicios({ data }) {
  const [sort, setSort] = useState({ field: 'total_utilidad', dir: 'desc' });

  function onSort(field) {
    setSort(s => s.field === field
      ? { field, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { field, dir: 'desc' }
    );
  }

  const rows = sortRows(Array.isArray(data) ? data : [], sort);
  const maxValor    = rows.reduce((m, r) => Math.max(m, parseFloat(r.total_valor) || 0), 1);
  const maxUtilidad = rows.reduce((m, r) => Math.max(m, parseFloat(r.total_utilidad) || 0), 1);

  return (
    <div>
      <div className="rent-tabla-desktop">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#5a7a8a', borderBottom: '1px solid var(--app-border)', textAlign: 'left' }}>
                Servicio
              </th>
              <SortHeader label="Registros"  field="registros"      sort={sort} onSort={onSort} />
              <SortHeader label="Facturado"  field="total_valor"    sort={sort} onSort={onSort} />
              <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--app-border)' }}></th>
              <SortHeader label="Utilidad"   field="total_utilidad" sort={sort} onSort={onSort} />
              <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--app-border)' }}></th>
              <SortHeader label="Margen"     field="margen_prom"    sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f4f7fa' }}>
                <td style={{ padding: '9px 10px', color: '#08293f', fontWeight: 600 }}>{r.servicio || '—'}</td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#5a7a8a' }}>{r.registros}</td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#08293f', fontWeight: 500 }}>{fmt(r.total_valor)}</td>
                <td style={{ padding: '9px 10px', width: 80 }}>
                  <Bar value={r.total_valor} max={maxValor} color="#00b8e3" />
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(r.total_utilidad)}</td>
                <td style={{ padding: '9px 10px', width: 80 }}>
                  <Bar value={r.total_utilidad} max={maxUtilidad} color="#059669" />
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 2,
                    background: r.margen_prom >= 50 ? 'rgba(5,150,105,0.08)' : r.margen_prom >= 20 ? 'rgba(251,191,36,0.1)' : 'rgba(248,113,113,0.08)',
                    color: r.margen_prom >= 50 ? '#059669' : r.margen_prom >= 20 ? '#b45309' : '#dc2626',
                  }}>{fmtPct(r.margen_prom)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rent-cards-mobile">
        {rows.map((r, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: '#08293f', fontSize: 13 }}>{r.servicio || '—'}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 2,
                background: r.margen_prom >= 50 ? 'rgba(5,150,105,0.08)' : 'rgba(251,191,36,0.1)',
                color: r.margen_prom >= 50 ? '#059669' : '#b45309',
              }}>{fmtPct(r.margen_prom)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11 }}>
              <div><span style={{ color: '#5a7a8a' }}>Facturado</span><br /><b style={{ color: '#08293f' }}>{fmt(r.total_valor)}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Utilidad</span><br /><b style={{ color: '#059669' }}>{fmt(r.total_utilidad)}</b></div>
              <div><span style={{ color: '#5a7a8a' }}>Registros</span><br /><b>{r.registros}</b></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Rentabilidad() {
  const [anio, setAnio]   = useState(currentYear);
  const [tab, setTab]     = useState('clientes');
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    API.get(`/rentabilidad?anio=${anio}`)
      .then(r => setData(r.data))
      .catch(e => { setErr(String(e)); setData({ porCliente: [], porServicio: [], porCategoria: [], porTipo: [] }); });
  }, [anio]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#08293f', margin: 0 }}>Rentabilidad</h1>
        <select value={anio} onChange={e => setAnio(e.target.value)} className="input" style={{ width: 'auto', fontSize: 12 }}>
          {ANIOS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {data && <TipoResumen data={data.porTipo} />}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--app-border)' }}>
        {[
          { key: 'clientes',   label: 'Por cliente'   },
          { key: 'servicios',  label: 'Por servicio'  },
          { key: 'categorias', label: 'Por categoría' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 16px', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
            fontFamily: "'Space Grotesk', sans-serif",
            background: 'transparent', border: 'none', cursor: 'pointer',
            borderBottom: tab === t.key ? '2px solid #00b8e3' : '2px solid transparent',
            color: tab === t.key ? '#00b8e3' : '#5a7a8a',
            marginBottom: -2, transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#dc2626' }}>{err}</div>}
      {!data ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#5a7a8a', fontSize: 13, letterSpacing: '0.06em' }}>CARGANDO...</div>
      ) : (
        <div className="card">
          {tab === 'clientes'   && <TabClientes   data={data.porCliente}   />}
          {tab === 'servicios'  && <TabServicios  data={data.porServicio}  />}
          {tab === 'categorias' && <TabCategorias data={data.porCategoria} />}
        </div>
      )}

      <style>{`
        .rent-tabla-desktop { display: block; overflow-x: auto; }
        .rent-cards-mobile  { display: none; }
        @media (max-width: 768px) {
          .rent-tabla-desktop { display: none; }
          .rent-cards-mobile  { display: block; }
        }
      `}</style>
    </div>
  );
}
